import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { Button } from './ui/button'
import { AddressSearch, type AddressData, type Iso20022Address } from './AddressSearch'
import { WizardStepHeader } from './WizardStepHeader'
import { useAssetsByProperty } from '../hooks/useQueries'

interface Location {
  lat: number
  lng: number
}

export type BuildingTypeChoice =
  | 'commercial'
  | 'residential_multi'
  | 'residential_single'

// Fields collected on step 1. The building-group `name` is no longer captured
// here — it is derived from the address by the orchestrator (AssetCreateWizard).
export interface BuildingGroupFormData {
  location: Location
  address: string
  iso20022?: Iso20022Address
  placeId?: string | null
  buildingName?: string | null
  buildingType?: BuildingTypeChoice
}

interface BuildingGroupCreateFormProps {
  org: string
  initialData?: BuildingGroupFormData
  /** When resuming/editing, the asset being edited — excluded from duplicate matches. */
  excludeAssetUid?: string
  onNext: (data: BuildingGroupFormData) => void
  onCancel: () => void
}

export function BuildingGroupCreateForm({ org, initialData, excludeAssetUid, onNext, onCancel }: BuildingGroupCreateFormProps) {
  const { t } = useTranslation('assets')
  const { t: tCommon } = useTranslation('common')
  const [formData, setFormData] = useState<BuildingGroupFormData>({
    location: initialData?.location || { lat: 48.8566, lng: 2.3522 },
    address: initialData?.address || '',
    iso20022: initialData?.iso20022,
    placeId: initialData?.placeId,
    buildingName: initialData?.buildingName,
    buildingType: initialData?.buildingType,
  })
  const [addressSelected, setAddressSelected] = useState(!!initialData?.address)

  useEffect(() => {
    if (initialData) {
      setFormData({
        location: initialData.location || { lat: 48.8566, lng: 2.3522 },
        address: initialData.address || '',
        iso20022: initialData.iso20022,
        placeId: initialData.placeId,
        buildingName: initialData.buildingName,
        buildingType: initialData.buildingType,
      })
      if (initialData.address) setAddressSelected(true)
    }
  }, [initialData])

  const handleAddressChange = useCallback((data: AddressData) => {
    setFormData(prev => ({
      ...prev,
      address: data.raw,
      location: data.geolocation || prev.location,
      iso20022: data.iso20022,
      placeId: data.placeId,
      buildingName: data.buildingName,
    }))
    if (data.placeId || data.geolocation) {
      setAddressSelected(true)
    }
  }, [])

  // Duplicate-address detection: match existing building groups on the raw address
  // string. address_raw is always persisted (unlike google_place_id, which is not
  // provisioned in every org), so it is the reliable dedup key. Non-blocking — the
  // user may still proceed.
  const dupValue = formData.address.trim()
  const { data: dupMatches = [] } = useAssetsByProperty(
    org,
    { assetTypeCode: 'building_group', propertyCode: 'address_raw', value: addressSelected ? dupValue : undefined },
    { enabled: addressSelected && !!dupValue }
  )
  const duplicates = useMemo(
    () => dupMatches.filter(a => a.uid !== excludeAssetUid),
    [dupMatches, excludeAssetUid]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (addressSelected && formData.buildingType) {
      onNext(formData)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card rounded-lg p-6">
        <WizardStepHeader step={1} title={t('create_building.group.step_locate')} />

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="building-group-details-form">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium mb-2">{t('create_building.address.title')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('create_building.address.description')}
              </p>
            </div>

            <AddressSearch
              initialRaw={formData.address}
              initialGeolocation={initialData?.location}
              initialAddress={formData.iso20022}
              initialPlaceId={formData.placeId || undefined}
              compact={true}
              showRawPreview={false}
              showDetails={false}
              onChange={handleAddressChange}
            />

            {formData.address && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm font-medium">{t('create_building.address.selected')}</p>
                <p className="text-sm text-muted-foreground">{formData.address}</p>
              </div>
            )}

            {duplicates.length > 0 && (
              <div
                className="p-3 rounded-md border border-destructive/50 bg-destructive/10 text-sm"
                data-testid="duplicate-address-warning"
              >
                <p className="font-medium text-destructive">
                  {t('create_building.address.duplicate_warning', { name: duplicates[0].name })}
                </p>
                <Link
                  to="/$org/assets/$assetUid"
                  params={{ org, assetUid: duplicates[0].uid }}
                  className="text-sm underline text-destructive"
                  data-testid="duplicate-address-link"
                >
                  {t('create_building.address.duplicate_view')}
                </Link>
              </div>
            )}
          </div>

          {addressSelected && (
            <fieldset>
              <legend className="block text-sm font-medium text-foreground mb-2">
                {t('create_building.group.building_type_label')}
              </legend>
              <div
                className="grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-2xl"
                data-testid="building-type-radio-group"
              >
                {(
                  [
                    { value: 'commercial', testid: 'building-type-commercial' },
                    { value: 'residential_multi', testid: 'building-type-residential-multi' },
                    { value: 'residential_single', testid: 'building-type-residential-single' },
                  ] as const
                ).map((option) => {
                  const checked = formData.buildingType === option.value
                  return (
                    <label
                      key={option.value}
                      className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="buildingType"
                        value={option.value}
                        checked={checked}
                        onChange={() =>
                          setFormData((prev) => ({ ...prev, buildingType: option.value }))
                        }
                        className="mt-1"
                        required
                        data-testid={option.testid}
                      />
                      <span className="text-sm">
                        {t(`create_building.group.building_type_${option.value}`)}
                      </span>
                    </label>
                  )
                })}
              </div>
              <p
                className="mt-2 text-xs text-muted-foreground"
                data-testid="building-type-mixed-use-hint"
              >
                {t('create_building.group.building_type_mixed_use_hint')}
              </p>
            </fieldset>
          )}

          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              data-testid="building-group-cancel-btn"
            >
              {tCommon('actions.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!addressSelected || !formData.buildingType}
              className="bg-primary hover:bg-primary/90"
              data-testid="building-group-continue-btn"
            >
              {tCommon('actions.continue')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
